import React from 'react';
import { cn } from '../../../lib/cn';
import { useFieldControl } from './Field';

/**
 * Checkbox, `.dsk-check`.
 *
 * Source selector: `system.css` `.dsk-check` (and its `:checked`, `::after`
 * glyph and `:focus-visible` rules).
 *
 * Doctrine enforced:
 * - It is a REAL `<input type="checkbox">` with `appearance: none`, never a
 *   styled `<div>`, so form semantics, the label association and the
 *   indeterminate state all come for free.
 * - Ticked is ACCENT, because a ticked filter is state, not a command. The tick
 *   glyph is a masked background from `--ds-check-glyph`, so it never drifts.
 * - The control stays 18px. Bigness for pointer targets comes from the row
 *   around it (`.dsk-facet__option`, `OptionRow`), never from inflating the box.
 *
 * `indeterminate` is a DOM property, not an attribute, so it is applied through
 * the ref the way the platform requires.
 */
export const Checkbox = React.forwardRef(function Checkbox(
  { indeterminate = false, className, children, ...rest },
  ref,
) {
  // Dropped on purpose, see the note in Switch.jsx: this is a bare <input>, a
  // void element, and children reaching it through the spread throws and takes
  // down the whole tree. Use <FacetOption> or pair it with a <Label>.
  if (process.env.NODE_ENV !== 'production' && children != null) {
    // eslint-disable-next-line no-console
    console.warn('Checkbox does not render children. Use <FacetOption> or pair it with a <Label>.');
  }

  const field = useFieldControl(rest);
  const innerRef = React.useRef(null);

  React.useImperativeHandle(ref, () => innerRef.current, []);

  React.useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <input
      ref={innerRef}
      type="checkbox"
      className={cn('dsk-check', className)}
      {...rest}
      id={field.id}
      aria-describedby={field['aria-describedby']}
    />
  );
});
Checkbox.displayName = 'Checkbox';

export default Checkbox;
